import { useEffect, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Share2, Download, Loader2, Check, Link2, Users, Send, Lock, Award } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import api from "@/lib/api";
import { generateBadgeBlob, BADGE_VARIANTS } from "@/lib/badge";

export default function BadgePreviewDialog({ open, onOpenChange, goal }) {
  const [variant, setVariant] = useState("olive");
  const [preview, setPreview] = useState(null);
  const [blob, setBlob] = useState(null);
  const [rendering, setRendering] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [ref, setRef] = useState(null);

  const refUrl = ref ? `${window.location.origin}/connexion?ref=${ref.code}` : "";
  const goldUnlocked = !!ref?.tier?.gold_unlocked;

  const selectVariant = (v) => {
    if (v.exclusive && !goldUnlocked) {
      toast.info("Parrainez 1 ami pour débloquer le badge Or « Ambassadeur ».");
      return;
    }
    setVariant(v.id);
  };

  const render = useCallback(async (v, url) => {
    setRendering(true);
    try {
      const b = await generateBadgeBlob(goal, v, url);
      setBlob(b);
      setPreview((old) => {
        if (old) URL.revokeObjectURL(old);
        return URL.createObjectURL(b);
      });
    } catch (_) {
      toast.error("Impossible de générer le badge.");
    } finally {
      setRendering(false);
    }
  }, [goal]);

  // fetch referral info once when opened
  useEffect(() => {
    if (open) {
      api.get("/referral").then(({ data }) => setRef(data)).catch(() => {});
    }
  }, [open]);

  // (re)render whenever variant, referral link or goal changes while open
  useEffect(() => {
    if (open && goal) render(variant, refUrl);
  }, [open, variant, goal, refUrl, render]);

  useEffect(() => {
    return () => { if (preview) URL.revokeObjectURL(preview); };
  }, [preview]);

  const countShare = async () => {
    try {
      const { data } = await api.post("/referral/share");
      setRef((r) => (r ? { ...r, shares: data.shares } : r));
    } catch (_) { /* non-blocking */ }
  };

  const download = () => {
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `badge-foodfend-${variant}.png`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Badge enregistré en image !");
    countShare();
  };

  const share = async () => {
    if (!blob) return;
    setSharing(true);
    try {
      const file = new File([blob], "badge-foodfend.png", { type: "image/png" });
      const text = `${goal.rate}% de produits sauvés en ${goal.month_label} avec FoodFend${
        goal.streak > 1 ? ` · série de ${goal.streak} mois` : ""
      } ! Rejoignez-moi 🌱 ${refUrl}`;
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Mon badge anti-gaspi FoodFend", text, url: refUrl });
        countShare();
      } else {
        download();
      }
    } catch (err) {
      if (err?.name !== "AbortError") download();
    } finally {
      setSharing(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(refUrl);
      toast.success("Lien de parrainage copié !");
    } catch (_) {
      toast.error("Copie impossible.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card sm:max-w-md max-h-[92vh] overflow-y-auto" data-testid="badge-preview-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Votre badge anti-gaspi</DialogTitle>
          <DialogDescription>Choisissez une couleur, partagez votre badge et invitez vos amis.</DialogDescription>
        </DialogHeader>

        <div className="relative rounded-md overflow-hidden border border-border bg-muted aspect-square">
          {preview && (
            <img src={preview} alt="Aperçu du badge" className="h-full w-full object-cover"
              data-testid="badge-preview-img" />
          )}
          {rendering && (
            <div className="absolute inset-0 flex items-center justify-center bg-card/50">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          )}
        </div>

        <div className="flex items-center justify-center gap-3 mt-1">
          {BADGE_VARIANTS.map((v) => {
            const locked = v.exclusive && !goldUnlocked;
            return (
              <button
                key={v.id}
                onClick={() => selectVariant(v)}
                data-testid={`badge-variant-${v.id}`}
                title={locked ? `${v.label} (verrouillé)` : v.label}
                className={cn(
                  "relative h-9 w-9 rounded-full border-2 transition-transform hover:scale-110",
                  variant === v.id ? "border-foreground" : "border-transparent",
                  locked && "opacity-60"
                )}
                style={{ backgroundColor: v.swatch }}
              >
                {variant === v.id && !locked && (
                  <Check className="h-4 w-4 absolute inset-0 m-auto text-white drop-shadow" />
                )}
                {locked && (
                  <Lock className="h-3.5 w-3.5 absolute inset-0 m-auto text-white drop-shadow" />
                )}
              </button>
            );
          })}
        </div>

        <div className="grid grid-cols-2 gap-3 mt-2">
          <Button variant="outline" className="rounded-full" onClick={download}
            disabled={!blob} data-testid="badge-download-btn">
            <Download className="h-4 w-4 mr-1.5" /> Télécharger
          </Button>
          <Button className="rounded-full" onClick={share} disabled={!blob || sharing}
            data-testid="badge-share-btn">
            {sharing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Share2 className="h-4 w-4 mr-1.5" />}
            Partager
          </Button>
        </div>

        {ref && (
          <div className="mt-3 rounded-md border border-border bg-muted/50 p-3" data-testid="referral-block">
            <div className="flex items-center justify-between gap-3 mb-2">
              <span className="mono text-[10px] uppercase tracking-mono text-muted-foreground">
                Lien de parrainage
              </span>
              <div className="flex items-center gap-3 mono text-[11px] text-muted-foreground">
                <span className="inline-flex items-center gap-1" data-testid="referral-shares">
                  <Send className="h-3 w-3" /> {ref.shares} partagé(s)
                </span>
                <span className="inline-flex items-center gap-1" data-testid="referral-count">
                  <Users className="h-3 w-3" /> {ref.referrals} ami(s)
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={refUrl}
                data-testid="referral-link-input"
                className="flex-1 min-w-0 rounded-md border border-border bg-background px-2.5 py-1.5 mono text-xs text-foreground"
              />
              <Button size="sm" variant="secondary" className="rounded-md shrink-0" onClick={copyLink}
                data-testid="referral-copy-btn">
                <Link2 className="h-3.5 w-3.5 mr-1" /> Copier
              </Button>
            </div>
          </div>
        )}

        {ref?.tier && (
          <div
            className={cn(
              "rounded-md border p-3 flex items-center gap-3",
              ref.tier.is_ambassador ? "border-[#EAB308]/50 bg-[#EAB308]/[0.08]" : "border-border bg-muted/40"
            )}
            data-testid="ambassador-card"
          >
            <span
              className={cn(
                "flex h-10 w-10 items-center justify-center rounded-full shrink-0",
                ref.tier.is_ambassador ? "bg-[#EAB308] text-white" : "bg-muted text-muted-foreground"
              )}
            >
              <Award className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              {ref.tier.is_ambassador ? (
                <>
                  <p className="font-semibold text-sm" data-testid="ambassador-tier-name">
                    Statut : {ref.tier.name} 🏆
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {ref.tier.next_name
                      ? `Encore ${ref.tier.remaining} filleul(s) pour devenir ${ref.tier.next_name}.`
                      : "Palier maximum atteint — merci de faire grandir FoodFend !"}
                  </p>
                </>
              ) : (
                <>
                  <p className="font-semibold text-sm">Devenez Ambassadeur</p>
                  <p className="text-xs text-muted-foreground">
                    Parrainez 1 ami pour débloquer le badge Or exclusif.
                  </p>
                </>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
